import React, { useCallback, useEffect, useRef } from 'react';

import { styled } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';

const ButtonWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
});

type GoogleSignInProps = {
  isSignIn?: boolean;
  setGoogleJwt: (credential: string) => void;
};
const GoogleSignIn = ({ isSignIn = true, setGoogleJwt }: GoogleSignInProps) => {
  const targetDivRef = useRef<HTMLDivElement>(null);
  const parentDivRef = useRef<HTMLDivElement>(null);

  const placeholderButton = (
    <Button color="alternate" disabled variant="outline" size="legacy-compact">
      Sign {isSignIn ? 'in' : 'up'} with Google
    </Button>
  );

  function handleCredentialResponse(
    response: google.accounts.id.CredentialResponse
  ) {
    setGoogleJwt(response.credential);
  }

  const initializeGoogleSignIn = useCallback(() => {
    if (window.google?.accounts?.id && window.IH.googleOAuthClientId) {
      window.google.accounts.id.initialize({
        client_id: window.IH.googleOAuthClientId,
        callback: handleCredentialResponse,
      });

      const targetDiv = targetDivRef.current;
      const parentDiv = parentDivRef.current;

      if (targetDiv && parentDiv) {
        window.google.accounts.id.renderButton(targetDiv, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: isSignIn ? 'signin_with' : 'signup_with',
          width: parentDiv.offsetWidth,
        });
      }
    }
  }, [isSignIn]);

  useEffect(() => {
    let mounted = true;

    const { onGoogleIdentityLoad } = window as any;
    if (onGoogleIdentityLoad) {
      onGoogleIdentityLoad(() => {
        if (mounted) {
          initializeGoogleSignIn();
        }
      });
    } else {
      throw new Error(
        'GoogleSignIn: window.onGoogleIdentityLoad is not defined. ' +
          'Make sure to include the _google_oauth.html.erb partial in your layout.'
      );
    }

    return () => {
      mounted = false;
    };
  }, [initializeGoogleSignIn]);

  return (
    <ButtonWrapper ref={parentDivRef}>
      <div ref={targetDivRef}>{placeholderButton}</div>
    </ButtonWrapper>
  );
};

export default GoogleSignIn;
